import { useState } from "react";
import { Phone, Pencil, Trash2, Plus, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";

const EMPTY = { name: "", relationship: "", phone: "" };

export function ContactsEditor({ clientId, contacts, onSaved }) {
  const [editIdx, setEditIdx] = useState(null); // index | "new" | null
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/clients/${clientId}/contacts`, { contacts: next });
      onSaved(data.contacts); setEditIdx(null); setF(EMPTY);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save contacts"); }
    finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    const next = editIdx === "new" ? [...contacts, f] : contacts.map((c, i) => (i === editIdx ? f : c));
    save(next);
  };
  const remove = (i) => { if (window.confirm(`Remove ${contacts[i].name} from emergency contacts?`)) save(contacts.filter((_, j) => j !== i)); };

  const form = (
    <form onSubmit={submit} className="space-y-1.5 py-1" data-testid="contact-form">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name *" autoFocus data-testid="contact-name" className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-primary" />
      <div className="grid grid-cols-2 gap-1.5">
        <input value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} placeholder="Relationship" data-testid="contact-relationship" className="h-8 rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-primary" />
        <PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })} placeholder="Phone" data-testid="contact-phone" className="h-8 rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-primary" />
      </div>
      <span className="flex gap-1 justify-end">
        <button type="submit" disabled={busy} data-testid="contact-save" className="h-8 w-8 rounded-md bg-primary text-white inline-flex items-center justify-center disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
        <button type="button" onClick={() => { setEditIdx(null); setF(EMPTY); }} data-testid="contact-cancel" className="h-8 w-8 rounded-md border border-slate-200 text-slate-500 inline-flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
      </span>
    </form>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3" data-testid="client-profile-contacts">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Emergency contacts & escorts</div>
        {editIdx === null && <button onClick={() => { setEditIdx("new"); setF(EMPTY); }} data-testid="contact-add-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Plus className="h-3 w-3" /> Add</button>}
      </div>
      {contacts.length === 0 && editIdx !== "new" && <div className="text-sm text-slate-400">None on file</div>}
      {contacts.map((c, i) => editIdx === i ? <div key={i}>{form}</div> : (
        <div key={i} className="group flex items-center justify-between gap-2 text-sm py-1 border-b border-slate-50 last:border-0" data-testid={`contact-row-${i}`}>
          <span className="font-medium text-slate-900 truncate">{c.name} <span className="text-slate-400 font-normal">{c.relationship}</span></span>
          <span className="flex items-center gap-1 shrink-0">
            {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-xs text-primary mr-1"><Phone className="h-3 w-3" /> {c.phone}</a>}
            <button onClick={() => { setEditIdx(i); setF({ ...EMPTY, ...c }); }} data-testid={`contact-edit-${i}`} title="Edit" className="h-7 w-7 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-800 inline-flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => remove(i)} data-testid={`contact-remove-${i}`} title="Remove" className="h-7 w-7 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 inline-flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      ))}
      {editIdx === "new" && form}
    </section>
  );
}
